import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M14_ROLES, hasAnyRole } from '../../lib/roles';
import type { Licence, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TABS = [
  { key: 'mine', label: 'My licences' },
  { key: 'all', label: 'All licences (staff)' },
] as const;

export function MyLicences() {
  const { persona } = useAuth();
  const canManage = hasAnyRole(persona?.roles, M14_ROLES.LICENSING_OFFICER, M14_ROLES.SENIOR_LO);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>(canManage ? 'all' : 'mine');

  return (
    <div>
      <PageHeader title="My / All Licences" subtitle="Holder view with expiry traffic-light, plus the staff register." />
      <Tabs tabs={canManage ? TABS : TABS.filter((t) => t.key === 'mine')} value={tab} onChange={setTab} />
      {tab === 'mine' ? <Mine /> : <All />}
    </div>
  );
}

function Mine() {
  const { persona } = useAuth();
  // Hook called unconditionally — see Permits.tsx's ApplyForm for why.
  const mineQ = useApiQuery<{ items: Licence[] }>(persona ? '/api/v1/m14/licences/me' : null, persona?.actingForBusinessId);
  if (!persona) return <InfoNote message="Sign in to view your licences." />;

  return (
    <div>
      {mineQ.error ? <ErrorNote message={mineQ.error} /> : null}
      {mineQ.loading ? <Spinner /> : (mineQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No licences yet" hint="Apply from Licensing › Applications." /> : (
        <div className="space-y-3">
          {mineQ.data!.items.map((l) => <LicenceCard key={l.id} licence={l} onChanged={mineQ.reload} />)}
        </div>
      )}
    </div>
  );
}

function LicenceCard({ licence, onChanged }: { licence: Licence; onChanged: () => void }) {
  const { persona } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intent, setIntent] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<string[] | null>(null);

  const renew = async () => {
    setBusy(true);
    setError(null);
    setBlockers(null);
    try {
      const res = await api.post<{ intent: { refNo: string } }>(
        `/api/v1/m14/licences/${licence.id}/renew`,
        {},
        { actingFor: licence.holderType === 'ENTITY' ? persona?.actingForBusinessId : undefined },
      );
      setIntent(res.intent.refNo);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'RENEWAL_BLOCKED') {
        setBlockers((e.details as { blockers?: string[] })?.blockers ?? []);
      } else {
        setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Renewal failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link to={`/licensing/licences/${licence.id}`} className="card block p-4 hover:border-brand-300">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-sm font-semibold">{licence.serial}</div>
          <div className="text-xs text-slate-500">{licence.classCode.replaceAll('_', ' ')}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {licence.trafficLight ? <StatusBadge status={licence.trafficLight} /> : null}
          <StatusBadge status={licence.status} />
        </div>
      </div>
      <div className="mt-1 text-xs text-slate-500">Valid to {formatDate(licence.validTo)}</div>
      {error ? <div className="mt-2"><ErrorNote message={error} /></div> : null}
      {blockers && blockers.length > 0 ? <div className="mt-2"><ErrorNote message={`Renewal blocked: ${blockers.join(', ')}`} /></div> : null}
      {intent ? (
        <div className="mt-2"><InfoNote message={`Renewal fee intent ${intent} raised — pay via SeyPay to complete.`} /></div>
      ) : (
        <button
          className="btn-secondary mt-2 !px-2.5 !py-1 text-xs"
          onClick={(e) => { e.preventDefault(); renew(); }}
          disabled={busy}
        >
          {busy ? 'Checking…' : 'Renew'}
        </button>
      )}
    </Link>
  );
}

function All() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  if (q) query.set('q', q);
  const listQ = useApiQuery<Page<Licence>>(`/api/v1/m14/licences?${query.toString()}`);

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option><option value="SUSPENDED">Suspended</option><option value="REVOKED">Revoked</option><option value="EXPIRED">Expired</option>
          </select>
        </Field>
        <Field label="Search" className="!mb-0"><input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Serial or holder id…" /></Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? <Spinner /> : (listQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No licences match" /> : (
          <table className="w-full">
            <thead><tr><th className="table-head">Serial</th><th className="table-head">Class</th><th className="table-head">Holder</th><th className="table-head">Status</th></tr></thead>
            <tbody>
              {listQ.data!.items.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs"><Link className="text-brand-600 hover:underline" to={`/licensing/licences/${l.id}`}>{l.serial}</Link></td>
                  <td className="table-cell text-xs">{l.classCode.replaceAll('_', ' ')}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{l.holderId}</td>
                  <td className="table-cell"><StatusBadge status={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
